import { useEffect, useState, type ReactNode } from "react";
import {
  Bell,
  Archive,
  ChevronDown,
  CircleUserRound,
  LogOut,
  Menu,
  MessageSquare,
  Plus,
  Settings,
  Users,
  X,
  Trash2,
} from "lucide-react";
import { Link, useNavigate } from "react-router";
import { apiFetch, ensureCsrf } from "@contractor-quoting/api-client";
import { Button } from "@contractor-quoting/shared-ui";

interface ProjectListItem {
  id: string;
  title: string | null;
  project_type: string;
  job_type_name?: string | null;
  status: string;
  can_delete?: boolean;
}

interface CustomerNotification {
  id: string;
  title: string;
  body: string;
  href: string | null;
  read_at: string | null;
  created_at: string;
}

export function CustomerWorkspaceShell({
  children,
  activeProjectId,
}: {
  children: ReactNode;
  activeProjectId?: string;
}) {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<ProjectListItem[]>([]);
  const [menu, setMenu] = useState(false);
  const [mobile, setMobile] = useState(false);
  const [notifications, setNotifications] = useState<CustomerNotification[]>([]);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  useEffect(() => {
    void apiFetch<{ items: ProjectListItem[] }>("/v1/customer/projects")
      .then((result) => setProjects(result.items))
      .catch(() => undefined);
    void apiFetch<{ items: CustomerNotification[] }>("/v1/customer/notifications")
      .then((result) => setNotifications(Array.isArray(result.items) ? result.items : []))
      .catch(() => undefined);
  }, [activeProjectId]);
  const openNotification = async (notification: CustomerNotification) => {
    if (!notification.read_at) {
      await ensureCsrf();
      await apiFetch(`/v1/customer/notifications/${notification.id}/read`, {
        method: "POST",
      });
      setNotifications((current) =>
        current.map((item) =>
          item.id === notification.id ? { ...item, read_at: new Date().toISOString() } : item,
        ),
      );
    }
    setNotificationsOpen(false);
    if (notification.href) void navigate(notification.href);
  };
  const removeFromList = async (project: ProjectListItem) => {
    await ensureCsrf();
    const title = project.title ?? "Untitled project";
    if (project.can_delete) {
      const confirmationTitle = window.prompt(
        `Delete “${title}”? Its chat and uploaded draft material will be permanently removed.\n\nType the project name to continue:`,
      );
      if (confirmationTitle !== title) return;
      await apiFetch(`/v1/customer/projects/${project.id}`, {
        method: "DELETE",
        body: JSON.stringify({ confirmationTitle }),
      });
    } else {
      if (!window.confirm(`Archive “${title}”? You can restore it later.`)) return;
      await apiFetch(`/v1/customer/projects/${project.id}/archive`, { method: "POST" });
    }
    setProjects((current) => current.filter((item) => item.id !== project.id));
    if (activeProjectId === project.id) void navigate("/projects");
  };
  const logout = async () => {
    await apiFetch("/v1/auth/logout", { method: "POST" }).catch(() => undefined);
    void navigate("/login");
  };
  return (
    <div className="grid h-dvh min-h-0 grid-cols-1 overflow-hidden bg-stone-100 md:grid-cols-[280px_minmax(0,1fr)]">
      <aside
        className={`${mobile ? "fixed inset-y-0 left-0 z-30 block w-72" : "hidden"} flex min-h-0 flex-col border-r border-stone-200 bg-stone-950 text-white md:flex`}
      >
        <div className="flex h-20 items-center justify-between border-b border-white/10 px-5">
          <Link to="/projects" className="flex items-center gap-3">
            <span className="h-11 w-11 overflow-hidden rounded-xl border border-white/15 bg-white shadow-sm">
              <img
                alt=""
                aria-hidden="true"
                className="h-full w-full object-cover"
                src="/brand/mziq-icon.png"
              />
            </span>
            <span>
              <span className="block text-xl font-black tracking-tight">Mezoniq</span>
              <span className="block text-xs font-semibold text-stone-400">Customer dashboard</span>
            </span>
          </Link>
          <button className="md:hidden" onClick={() => setMobile(false)}>
            <X />
          </button>
        </div>
        <div className="p-3">
          <Button className="w-full justify-start" onClick={() => void navigate("/chat")}>
            <Plus size={18} /> New project
          </Button>
        </div>
        <nav className="min-h-0 flex-1 overflow-y-auto px-3">
          <Link
            className="flex items-center gap-3 rounded-xl px-3 py-3 font-semibold text-stone-200 hover:bg-white/10"
            to="/projects/contractors"
          >
            <Users size={18} /> Find a contractor
          </Link>
          <p className="mb-2 mt-6 px-3 text-xs font-bold uppercase tracking-widest text-stone-500">
            Projects & chats
          </p>
          {projects.map((project) => (
            <div
              key={project.id}
              className={`mb-1 flex items-center rounded-xl ${activeProjectId === project.id ? "bg-white text-stone-950" : "text-stone-300 hover:bg-white/10"}`}
            >
              <Link
                to={`/projects/${project.id}`}
                className="flex min-w-0 flex-1 items-center gap-3 px-3 py-3 text-sm"
              >
                <MessageSquare size={17} />
                <span className="truncate">
                  {project.title ||
                    project.job_type_name ||
                    project.project_type.replaceAll("_", " ")}
                </span>
              </Link>
              <button
                className="mr-1 rounded-lg p-2 opacity-70 hover:bg-black/10 hover:opacity-100"
                aria-label={`Remove ${project.title ?? "project"}`}
                onClick={() => void removeFromList(project)}
                type="button"
              >
                {project.can_delete ? <Trash2 size={15} /> : <Archive size={15} />}
              </button>
            </div>
          ))}
          <Link
            className="mt-4 flex items-center gap-3 rounded-xl px-3 py-3 text-sm text-stone-400 hover:bg-white/10"
            to="/projects?archived=true"
          >
            <Archive size={17} /> Archived
          </Link>
        </nav>
        <div className="border-t border-white/10 p-3">
          <button
            className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm text-stone-300 hover:bg-white/10"
            onClick={() => void logout()}
          >
            <LogOut size={18} /> Log out
          </button>
        </div>
      </aside>
      <div className="flex h-full min-h-0 min-w-0 flex-col">
        <header className="flex h-16 shrink-0 items-center justify-between border-b border-stone-200 bg-white px-4 sm:px-6">
          <button className="md:hidden" onClick={() => setMobile(true)}>
            <Menu />
          </button>
          <span className="hidden text-sm font-semibold text-stone-500 md:block">
            Customer dashboard
          </span>
          <div className="flex items-center gap-2">
            <div className="relative">
              <button
                className="relative rounded-full p-2 hover:bg-stone-100"
                aria-label="Notifications"
                aria-expanded={notificationsOpen}
                onClick={() => setNotificationsOpen((current) => !current)}
              >
                <Bell size={20} />
                {notifications.some((item) => !item.read_at) ? (
                  <span className="absolute right-1 top-1 h-2.5 w-2.5 rounded-full bg-emerald-600 ring-2 ring-white" />
                ) : null}
              </button>
              {notificationsOpen ? (
                <div className="absolute right-0 z-30 mt-2 max-h-96 w-80 overflow-y-auto rounded-xl border border-stone-200 bg-white p-2 shadow-xl">
                  <p className="px-3 py-2 text-xs font-bold uppercase tracking-widest text-stone-500">
                    Notifications
                  </p>
                  {notifications.length === 0 ? (
                    <p className="px-3 py-4 text-sm text-stone-500">No notifications yet.</p>
                  ) : (
                    notifications.map((notification) => (
                      <button
                        className={`w-full rounded-lg px-3 py-3 text-left hover:bg-stone-100 ${
                          notification.read_at ? "text-stone-500" : "bg-emerald-50 text-stone-950"
                        }`}
                        key={notification.id}
                        onClick={() => void openNotification(notification)}
                        type="button"
                      >
                        <span className="block text-sm font-bold">{notification.title}</span>
                        <span className="mt-1 block text-sm">{notification.body}</span>
                      </button>
                    ))
                  )}
                </div>
              ) : null}
            </div>
            <div className="relative">
              <button
                className="flex items-center gap-2 rounded-xl border border-stone-200 px-3 py-2 font-semibold"
                onClick={() => setMenu(!menu)}
              >
                <CircleUserRound size={20} />
                <span className="hidden sm:inline">Account</span>
                <ChevronDown size={15} />
              </button>
              {menu ? (
                <div className="absolute right-0 z-20 mt-2 w-48 rounded-xl border border-stone-200 bg-white p-2 shadow-xl">
                  <Link
                    className="flex items-center gap-2 rounded-lg px-3 py-2 hover:bg-stone-100"
                    to="/profile"
                  >
                    <Settings size={17} /> My profile
                  </Link>
                  <button
                    className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left hover:bg-stone-100"
                    onClick={() => void logout()}
                  >
                    <LogOut size={17} /> Log out
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">{children}</div>
      </div>
    </div>
  );
}
